import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';

function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleClick = (n) => {
    if (!n.is_read) markRead(n.id);
    if (n.data?.request_id) navigate(`/requests/${n.data.request_id}`);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', marginLeft: '6px' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Notifications"
        style={{
          position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '34px', height: '34px', borderRadius: '50%', border: '1px solid var(--gray-200)',
          background: open ? 'var(--gray-100)' : 'var(--white)', cursor: 'pointer',
        }}
      >
        <Bell size={16} color="var(--gray-600)" strokeWidth={1.9} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: '-3px', right: '-3px', minWidth: '16px', height: '16px',
            borderRadius: '9px', background: 'var(--red)', color: 'white', fontSize: '10px',
            fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', border: '2px solid white',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '42px', width: '320px', maxWidth: '90vw',
          background: 'var(--white)', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-md)', zIndex: 300, overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '11px 14px', borderBottom: '1px solid var(--gray-100)',
          }}>
            <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--gray-900)' }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{ background: 'none', border: 'none', color: 'var(--red)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--gray-400)', fontSize: '13px' }}>
                No notifications yet.
              </div>
            ) : notifications.map(n => (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                style={{
                  display: 'flex', gap: '10px', padding: '12px 14px', cursor: 'pointer',
                  borderBottom: '1px solid var(--gray-100)',
                  background: n.is_read ? 'transparent' : 'var(--red-light)',
                }}
              >
                <span style={{
                  width: '7px', height: '7px', borderRadius: '50%', marginTop: '5px', flexShrink: 0,
                  background: n.is_read ? 'transparent' : 'var(--red)',
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-900)' }}>{n.title}</div>
                  {n.body && <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px', lineHeight: 1.4 }}>{n.body}</div>}
                  <div style={{ fontSize: '11px', color: 'var(--gray-400)', marginTop: '4px' }}>{timeAgo(n.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
